import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "../components/Icon";
import { Num } from "../components/Num";
import {
  cleanItemNumber,
  displayItemNumber,
  groupItemsBySection,
  orderSelectedIds
} from "../pickerGrouping";
import {
  closedAgendaItemPresentation,
  resolveClosedReason
} from "../agendaItemMessages";
import { offeredStances } from "../dispositionOptions";
import type { AgendaItem, AgendaSection, Stance } from "../types";
import type { IconName } from "../components/Icon";

interface ListNavProps {
  listRef: React.MutableRefObject<HTMLDivElement | null>;
  itemCount: number;
}

const ListNav: React.FC<ListNavProps> = ({ listRef, itemCount }) => {
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(false);

  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const longPressed = useRef(false);
  const LONG_PRESS_THRESHOLD_MS = 320;
  const TICK_PX = 24;
  const TICK_MS = 16;

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const update = () => {
      const top = el.scrollTop;
      const max = el.scrollHeight - el.clientHeight;
      setAtTop(top <= 2);
      setAtBottom(max <= 0 || top >= max - 2);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [listRef, itemCount]);

  useEffect(() => {
    return () => {
      if (pressTimer.current) clearTimeout(pressTimer.current);
      if (tickTimer.current) clearInterval(tickTimer.current);
    };
  }, []);

  const page = (direction: 1 | -1) => {
    const el = listRef.current;
    if (!el) return;
    const delta = el.clientHeight * 0.85 * direction;
    el.scrollBy({ top: delta, behavior: "smooth" });
  };

  const startPress = (direction: 1 | -1) => (e: React.PointerEvent) => {
    longPressed.current = false;
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* pointer-capture not supported */
    }
    if (pressTimer.current) clearTimeout(pressTimer.current);
    if (tickTimer.current) clearInterval(tickTimer.current);
    pressTimer.current = setTimeout(() => {
      longPressed.current = true;
      tickTimer.current = setInterval(() => {
        const el = listRef.current;
        if (!el) return;
        el.scrollBy({ top: TICK_PX * direction });
      }, TICK_MS);
    }, LONG_PRESS_THRESHOLD_MS);
  };

  const endPress = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
    if (tickTimer.current) {
      clearInterval(tickTimer.current);
      tickTimer.current = null;
    }
  };

  const handleClick = (direction: 1 | -1) => () => {
    if (longPressed.current) {
      longPressed.current = false;
      return;
    }
    page(direction);
  };

  return (
    <div className="list-nav">
      <button
        type="button"
        className="list-nav-btn up"
        onClick={handleClick(-1)}
        onPointerDown={startPress(-1)}
        onPointerUp={endPress}
        onPointerCancel={endPress}
        onPointerLeave={endPress}
        disabled={atTop}
        aria-label="Scroll up — tap once for a page, hold to scroll continuously"
      >
        <Icon name="chevron-up" size={36} />
      </button>
      <button
        type="button"
        className="list-nav-btn down"
        onClick={handleClick(1)}
        onPointerDown={startPress(1)}
        onPointerUp={endPress}
        onPointerCancel={endPress}
        onPointerLeave={endPress}
        disabled={atBottom}
        aria-label="Scroll down — tap once for a page, hold to scroll continuously"
      >
        <Icon name="chevron-down" size={36} />
      </button>
    </div>
  );
};

export interface PickerProps {
  agendaItems: AgendaItem[];
  sections: AgendaSection[];
  selections: Record<number, Stance>;
  setSelections: React.Dispatch<React.SetStateAction<Record<number, Stance>>>;
  /** Fired only from the resident's own taps, with the state the item ends up in. App turns an
   *  "off" toggle into a stated withdrawal (Bug #10369); nothing that changes `selections`
   *  behind their back - the status poll pruning a closed item, say - goes through here. */
  onUserToggledItem: (id: number, nowSelected: boolean) => void;
  collectDisposition: boolean;
  dispositionOptions?: number[] | null;
  maxItemsPerSpeaker?: number;
}

export const Picker: React.FC<PickerProps> = ({
  agendaItems,
  sections,
  selections,
  setSelections,
  onUserToggledItem,
  collectDisposition,
  dispositionOptions,
  maxItemsPerSpeaker
}) => {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "open" | "mine">("all");
  const [sheet, setSheet] = useState<null | "jump" | "picks">(null);
  const [jump, setJump] = useState("");
  const [jumpMode, setJumpMode] = useState<"123" | "abc">("123");
  const listRef = useRef<HTMLDivElement | null>(null);

  const openCount = useMemo(
    () => agendaItems.filter((it) => it.acceptingSpeakers !== false).length,
    [agendaItems]
  );

  const filtered = useMemo(() => {
    let items = agendaItems;
    const q = query.trim().replace(/^#+/, "").toLowerCase();
    if (q) {
      items = items.filter(
        (it) =>
          String(it.id).startsWith(q) ||
          it.title.toLowerCase().includes(q) ||
          cleanItemNumber(it.itemNumber).toLowerCase().includes(q)
      );
    }
    if (filter === "mine") items = items.filter((it) => selections[it.id]);
    else if (filter === "open")
      items = items.filter((it) => it.acceptingSpeakers !== false);
    return items;
  }, [query, filter, selections, agendaItems]);

  const groups = useMemo(
    () => groupItemsBySection(filtered, sections),
    [filtered, sections]
  );

  // Bug #10369: a tap the guards inside setSelections reject is not a toggle. Reporting a
  // rejected re-select as "selected again" would drop the id from removedItemIds and lose the
  // withdrawal the resident already made.
  // Read through a ref, not the render-time prop. useStatusPoll prunes newly-closed items via
  // setSelections, and between that call and the commit the prop is stale: a tap in that window
  // would look like a deselect of a still-held item and be recorded as a withdrawal, while the
  // updater rejects the change. The row would then be deleted and never re-created.
  const selectionsRef = useRef(selections);
  selectionsRef.current = selections;

  // AB#11358: per-section cap on items one person can pick, applied alongside the
  // meeting-wide maxItemsPerSpeaker. Null, zero or negative = no limit for that section.
  const sectionCapById = useMemo(() => {
    const caps = new Map<number, number>();
    for (const s of sections) {
      const cap = s.maxAgendaItemsPerRegistrant;
      if (cap != null && cap > 0) caps.set(s.id, cap);
    }
    return caps;
  }, [sections]);

  const sectionIdByItemId = useMemo(
    () => new Map(agendaItems.map((a) => [a.id, a.sectionId] as const)),
    [agendaItems]
  );

  /** The cap of the item's section when the given selections already fill it, else null. */
  const fullSectionCap = (
    current: Record<number, Stance>,
    id: number
  ): number | null => {
    const sectionId = sectionIdByItemId.get(id);
    if (sectionId == null) return null;
    const cap = sectionCapById.get(sectionId);
    if (cap == null) return null;
    const pickedInSection = Object.keys(current).filter(
      (key) => sectionIdByItemId.get(Number(key)) === sectionId
    ).length;
    return pickedInSection >= cap ? cap : null;
  };

  // Picked count per capped section, for render. The updaters above use fullSectionCap
  // against the state they are handed, never this render-time snapshot.
  const pickedBySectionId = useMemo(() => {
    const counts = new Map<number, number>();
    if (sectionCapById.size === 0) return counts;
    for (const key of Object.keys(selections)) {
      const sectionId = sectionIdByItemId.get(Number(key));
      if (sectionId != null && sectionCapById.has(sectionId)) {
        counts.set(sectionId, (counts.get(sectionId) ?? 0) + 1);
      }
    }
    return counts;
  }, [selections, sectionIdByItemId, sectionCapById]);

  // Announce reaching a section cap through one polite live region. Only a section that
  // becomes full is announced; an unchanged state never re-announces.
  const [sectionCapAnnouncement, setSectionCapAnnouncement] = useState("");
  const fullSectionIdsRef = useRef<Set<number> | null>(null);
  useEffect(() => {
    const full = new Set<number>();
    sectionCapById.forEach((cap, sectionId) => {
      if ((pickedBySectionId.get(sectionId) ?? 0) >= cap) full.add(sectionId);
    });
    // The first run records the starting state: a returning resident who is already at a
    // cap hears nothing until something changes.
    const previous = fullSectionIdsRef.current;
    fullSectionIdsRef.current = full;
    if (previous == null) return;
    let newlyFull: number | undefined;
    full.forEach((sectionId) => {
      if (!previous.has(sectionId)) newlyFull = sectionId;
    });
    if (newlyFull != null) {
      const cap = sectionCapById.get(newlyFull) ?? 0;
      const title = sections.find((s) => s.id === newlyFull)?.title?.trim();
      setSectionCapAnnouncement(
        `You've reached the limit of ${cap} ${cap === 1 ? "item" : "items"} in ${
          title ? `the ${title} section` : "this section"
        }.`
      );
    } else if (full.size === 0) {
      setSectionCapAnnouncement("");
    }
  }, [pickedBySectionId, sectionCapById, sections]);

  const selectionRejected = (id: number, it?: AgendaItem) => {
    const current = selectionsRef.current;
    if (current[id] != null) return false;
    if (it && it.acceptingSpeakers === false) return true;
    if (
      maxItemsPerSpeaker != null &&
      Object.keys(current).length >= maxItemsPerSpeaker
    ) {
      return true;
    }
    return fullSectionCap(current, id) != null;
  };

  const setStance = (id: number, stance: Stance) => {
    const it = agendaItems.find((a) => a.id === id);
    // Read the outcome off the rendered state, not from inside the updater below: React may
    // call an updater more than once, and reporting a toggle twice would be wrong.
    const nowSelected = selectionsRef.current[id] !== stance;
    const rejected = selectionRejected(id, it);
    setSelections((s) => {
      const isNew = s[id] == null;
      if (isNew) {
        if (it && it.acceptingSpeakers === false) return s;
        if (
          maxItemsPerSpeaker != null &&
          Object.keys(s).length >= maxItemsPerSpeaker
        ) {
          return s;
        }
        if (fullSectionCap(s, id) != null) return s;
      }
      const next = { ...s };
      if (next[id] === stance) delete next[id];
      else next[id] = stance;
      return next;
    });
    if (!rejected) onUserToggledItem(id, nowSelected);
  };

  const toggleSignup = (id: number) => {
    const it = agendaItems.find((a) => a.id === id);
    const nowSelected = selectionsRef.current[id] == null;
    const rejected = selectionRejected(id, it);
    setSelections((s) => {
      const isNew = s[id] == null;
      if (isNew) {
        if (it && it.acceptingSpeakers === false) return s;
        if (
          maxItemsPerSpeaker != null &&
          Object.keys(s).length >= maxItemsPerSpeaker
        ) {
          return s;
        }
        if (fullSectionCap(s, id) != null) return s;
      }
      const next = { ...s };
      if (next[id]) delete next[id];
      else next[id] = "neutral";
      return next;
    });
    if (!rejected) onUserToggledItem(id, nowSelected);
  };

  const doJump = () => {
    const raw = jump.trim();
    if (!raw) return;
    const upper = raw.toUpperCase();
    const target =
      agendaItems.find(
        (it) => cleanItemNumber(it.itemNumber).toUpperCase() === upper
      ) ??
      (() => {
        const n = parseInt(raw, 10);
        if (!n || n < 1 || String(n) !== raw) return undefined;
        return agendaItems.find((it) => {
          const prefix = cleanItemNumber(it.itemNumber).match(/^\d+/)?.[0];
          return prefix && parseInt(prefix, 10) === n;
        });
      })();
    setQuery("");
    setFilter("all");
    setSheet(null);
    setJump("");
    if (!target) return;
    window.setTimeout(() => {
      const el = listRef.current?.querySelector<HTMLElement>(
        `[data-item-id="${target.id}"]`
      );
      if (el && listRef.current) {
        listRef.current.scrollTo({
          top: el.offsetTop - 80,
          behavior: "smooth"
        });
        el.classList.add("flash");
        window.setTimeout(() => el.classList.remove("flash"), 1200);
      }
    }, 30);
  };

  const tapKey = (k: string) => {
    if (k === "del") setJump((j) => j.slice(0, -1));
    else if (k === "clr") setJump("");
    else if (jump.length < 6) {
      setJump((j) => {
        const next = j + k;
        return /^\d+$/.test(next) ? next.replace(/^0+(?=\d)/, "") : next;
      });
    }
  };

  const stanceLabels: Record<Stance, string> = {
    for: "Speak in favor",
    against: "Speak against",
    neutral: "Speak neutral"
  };
  const stanceIcons: Record<Stance, IconName> = {
    for: "thumbs-up",
    against: "thumbs-down",
    neutral: "minus"
  };
  const stances = offeredStances(dispositionOptions);
  const selectedCount = Object.keys(selections).length;

  return (
    <div className="picker">
      <p className="sr-only" role="status" aria-live="polite">
        {sectionCapAnnouncement}
      </p>
      <div className="search-row">
        <div className="search">
          <Icon name="search" size={24} />
          <input
            placeholder="Search by keyword, item number, or code (e.g. CS-1)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search agenda"
          />
          {query && (
            <button
              type="button"
              className="clear"
              onClick={() => setQuery("")}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
        <button
          type="button"
          className="jump-btn"
          onClick={() => setSheet("jump")}
          aria-label="Jump to item number"
        >
          <Icon name="keypad" size={22} />
          <span>Jump to #</span>
        </button>
      </div>

      <div className="filters" role="tablist">
        <button
          type="button"
          className={`filter ${filter === "all" ? "on" : ""}`}
          onClick={() => setFilter("all")}
        >
          All items <Num className="pill">{agendaItems.length}</Num>
        </button>
        <button
          type="button"
          className={`filter ${filter === "open" ? "on" : ""}`}
          onClick={() => setFilter("open")}
          disabled={agendaItems.length === 0}
        >
          Open for signup <Num className="pill">{openCount}</Num>
        </button>
        <button
          type="button"
          className={`filter ${filter === "mine" ? "on" : ""}`}
          onClick={() => setFilter("mine")}
          disabled={!selectedCount}
        >
          My picks <Num className="pill">{selectedCount}</Num>
        </button>
      </div>

      <div className="list" ref={listRef}>
        {filtered.length === 0 && (
          <div className="empty-state">
            <p className="big">
              {query
                ? `No items match "${query}"`
                : "No agenda items for this meeting yet"}
            </p>
            {query && (
              <p>
                Try a different keyword, or tap <strong>All items</strong>{" "}
                above.
              </p>
            )}
          </div>
        )}
        {groups.map((g) => (
          <div
            key={g.key}
            role={g.label ? "group" : undefined}
            aria-labelledby={g.label ? `${g.key}-head` : undefined}
          >
            {g.label && (
              <div
                className="group-head"
                id={`${g.key}-head`}
                role="heading"
                aria-level={2}
              >
                {g.label}
              </div>
            )}
            {(() => {
              const sectionCap =
                g.key === "unsectioned" || g.items.length === 0
                  ? undefined
                  : sectionCapById.get(g.items[0].sectionId);
              if (sectionCap == null) return null;
              return (
                <div className="section-limit-note">
                  Up to <Num>{sectionCap}</Num>{" "}
                  {sectionCap === 1 ? "item" : "items"} in this section.
                </div>
              );
            })()}
            {g.items.map((it) => {
              const stance = selections[it.id];
              const locked = it.acceptingSpeakers === false;
              const closedPresentation = closedAgendaItemPresentation(
                resolveClosedReason(it)
              );
              const meetingLimitBlocked =
                !stance &&
                maxItemsPerSpeaker != null &&
                selectedCount >= maxItemsPerSpeaker;
              const blockingSectionCap =
                !stance && !meetingLimitBlocked
                  ? (() => {
                      const sectionId = sectionIdByItemId.get(it.id);
                      const cap =
                        sectionId == null ? undefined : sectionCapById.get(sectionId);
                      return cap != null &&
                        (pickedBySectionId.get(sectionId!) ?? 0) >= cap
                        ? cap
                        : null;
                    })()
                  : null;
              const limitBlocked =
                meetingLimitBlocked || blockingSectionCap != null;
              const limitNote = meetingLimitBlocked ? (
                <div className="limit-note" id={`limit-note-${it.id}`}>
                  You've selected the maximum of {maxItemsPerSpeaker}{" "}
                  {maxItemsPerSpeaker === 1 ? "item" : "items"}. Remove
                  one to choose this instead.
                </div>
              ) : blockingSectionCap != null ? (
                <div className="limit-note" id={`limit-note-${it.id}`}>
                  You've reached the limit of <Num>{blockingSectionCap}</Num>{" "}
                  {blockingSectionCap === 1 ? "item" : "items"} in this section.
                  Remove one to choose this instead.
                </div>
              ) : null;
              const showLocked = locked && !stance;
              return (
                <div
                  key={it.id}
                  className={`item ${stance ? `selected ${stance}` : ""} ${
                    showLocked ? "locked" : ""
                  }`}
                  data-item-id={it.id}
                  data-accepting-speakers={
                    it.acceptingSpeakers === false ? "false" : "true"
                  }
                >
                  <div className="item-head">
                    <div className="num">
                      <Num>{displayItemNumber(it.itemNumber, it.id)}</Num>
                    </div>
                    <div className="title-wrap">
                      <p className="title">{it.title}</p>
                      {showLocked && closedPresentation && (
                        <span
                          className="locked-badge"
                          aria-label={closedPresentation.accessibleName}
                        >
                          <Icon name="lock" size={14} />{" "}
                          {closedPresentation.badgeText}
                        </span>
                      )}
                    </div>
                  </div>
                  {showLocked && closedPresentation ? (
                    <div className="locked-note">{closedPresentation.note}</div>
                  ) : collectDisposition ? (
                    <>
                      <div
                        className={`stances ${
                          limitBlocked ? "limit-blocked" : ""
                        }`}
                      >
                        {stances.map((option) => (
                          <button
                            key={option}
                            type="button"
                            className={`stance ${option} ${
                              stance === option ? "on" : ""
                            }`}
                            onClick={() => setStance(it.id, option)}
                            aria-pressed={stance === option}
                            disabled={limitBlocked}
                            aria-describedby={
                              limitBlocked ? `limit-note-${it.id}` : undefined
                            }
                          >
                            <span className="icon">
                              <Icon name={stanceIcons[option]} size={22} />
                            </span>
                            {stanceLabels[option]}
                          </button>
                        ))}
                      </div>
                      {limitNote}
                    </>
                  ) : (
                    <>
                      <div
                        className={`stances solo ${
                          limitBlocked ? "limit-blocked" : ""
                        }`}
                      >
                        <button
                          type="button"
                          className={`stance signup ${stance ? "on" : ""}`}
                          onClick={() => toggleSignup(it.id)}
                          aria-pressed={!!stance}
                          disabled={limitBlocked}
                          aria-describedby={
                            limitBlocked ? `limit-note-${it.id}` : undefined
                          }
                        >
                          <span className="icon">
                            <Icon name={stance ? "check" : "plus"} size={22} />
                          </span>
                          {stance ? "Signed up to speak" : "Sign up to speak"}
                        </button>
                      </div>
                      {limitNote}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
        <div style={{ height: 24 }} />
      </div>

      <ListNav listRef={listRef} itemCount={filtered.length} />

      {sheet === "jump" && (
        <div className="sheet-scrim" onClick={() => setSheet(null)}>
          <div
            className="sheet jump-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sheet-head">
              <h3>Jump to item</h3>
              <button
                type="button"
                className="sheet-close"
                onClick={() => setSheet(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="sheet-sub">
              Type the item number (e.g. <strong>42</strong>) or agenda code
              (e.g. <strong>CS-1</strong>, <strong>1-D</strong>).
            </p>
            <div className={`jumpdisplay ${!jump ? "empty" : ""}`}>
              {jump || "—"}
            </div>

            <div className="jump-mode" role="tablist" aria-label="Keypad mode">
              <button
                type="button"
                role="tab"
                aria-selected={jumpMode === "123"}
                className={jumpMode === "123" ? "on" : ""}
                onClick={() => setJumpMode("123")}
              >
                <Icon name="keypad" size={18} /> 123
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={jumpMode === "abc"}
                className={jumpMode === "abc" ? "on" : ""}
                onClick={() => setJumpMode("abc")}
              >
                <span className="abc-glyph">ABC</span> Letters
              </button>
            </div>

            {jumpMode === "123" ? (
              <div className="keypad">
                {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((k) => (
                  <button type="button" key={k} onClick={() => tapKey(k)}>
                    {k}
                  </button>
                ))}
                <button
                  type="button"
                  className="keypad-alt"
                  onClick={() => tapKey("-")}
                  aria-label="Dash"
                >
                  –
                </button>
                <button type="button" onClick={() => tapKey("0")}>
                  0
                </button>
                <button
                  type="button"
                  className="del"
                  onClick={() => tapKey("del")}
                  aria-label="Delete last digit"
                >
                  ⌫
                </button>
              </div>
            ) : (
              <div className="keypad-abc">
                {"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((k) => (
                  <button type="button" key={k} onClick={() => tapKey(k)}>
                    {k}
                  </button>
                ))}
                <button
                  type="button"
                  className="keypad-alt"
                  onClick={() => tapKey("-")}
                  aria-label="Dash"
                >
                  –
                </button>
                <button
                  type="button"
                  className="del"
                  onClick={() => tapKey("del")}
                  aria-label="Delete last"
                >
                  ⌫
                </button>
              </div>
            )}

            <div className="jump-foot">
              <button
                type="button"
                className="btn lg"
                onClick={() => tapKey("clr")}
                disabled={!jump}
              >
                Clear
              </button>
              <button
                type="button"
                className="btn lg primary"
                onClick={doJump}
                disabled={!jump}
              >
                Find item <Icon name="arrow-right" size={20} />
              </button>
            </div>
          </div>
        </div>
      )}

      {sheet === "picks" && (
        <div className="sheet-scrim" onClick={() => setSheet(null)}>
          <div
            className="sheet picks-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sheet-head">
              <h3>
                Your selections (<Num>{selectedCount}</Num>)
              </h3>
              <button
                type="button"
                className="sheet-close"
                onClick={() => setSheet(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="basket">
              {selectedCount === 0 ? (
                <div className="empty">
                  {collectDisposition
                    ? "Tap a stance on any item to add it here."
                    : "Tap Sign up to speak on any item to add it here."}
                </div>
              ) : (
                orderSelectedIds(selections, agendaItems, sections).map(
                  (id) => {
                    const it = agendaItems.find((x) => x.id === id);
                    if (!it) return null;
                    const s = selections[id];
                    return (
                      <div className="chip" key={id}>
                        <span className="n">
                          <Num>{displayItemNumber(it.itemNumber, it.id)}</Num>
                        </span>
                        <span
                          className={`dot ${collectDisposition ? s : "total"}`}
                        ></span>
                        <span className="label">{it.title}</span>
                        <button
                          type="button"
                          className="x"
                          onClick={() => setStance(id, s)}
                          aria-label={`Remove item ${id}`}
                        >
                          ×
                        </button>
                      </div>
                    );
                  }
                )
              )}
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        className={`picks-peek ${selectedCount ? "active" : ""}`}
        onClick={() => setSheet("picks")}
        disabled={!selectedCount}
      >
        <span className="dots">
          {collectDisposition ? (
            <>
              <span className="dot for">
                {Object.values(selections).filter((s) => s === "for").length}
              </span>
              <span className="dot against">
                {
                  Object.values(selections).filter((s) => s === "against")
                    .length
                }
              </span>
              <span className="dot neutral">
                {
                  Object.values(selections).filter((s) => s === "neutral")
                    .length
                }
              </span>
            </>
          ) : (
            <Num className="dot total">{selectedCount}</Num>
          )}
        </span>
        <span className="label">
          {selectedCount === 0
            ? "No items selected yet"
            : `${selectedCount} ${
                selectedCount === 1 ? "item" : "items"
              } selected — tap to review`}
        </span>
      </button>
    </div>
  );
};
